import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, Search, Save, Close } from '@mui/icons-material';
import { apiService } from '../services/api';

const PAGE_SIZE = 15;

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#4CAF50');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#666666');
  const [editSort, setEditSort] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const fetchCategories = async (q = '') => {
    setLoading(true);
    try {
      const res: any = await apiService.getArticleCategories(q || undefined);
      setCategories(res.data.categories || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCategories(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [search]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSubmitting(true);
    try {
      const res: any = await apiService.createArticleCategory({ name: newName.trim(), color: newColor });
      setCategories((prev) => [...prev, res.data.category].sort((a, b) => a.sort_order - b.sort_order));
      setNewName('');
      setNewColor('#4CAF50');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (cat: any) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
    setEditSort(cat.sort_order || 0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('#666666');
    setEditSort(0);
  };

  const handleSaveEdit = async (id: number) => {
    if (!editName.trim()) return;
    setSavingEdit(true);
    try {
      const res: any = await apiService.updateArticleCategory(id, { name: editName.trim(), color: editColor, sort_order: editSort });
      setCategories((prev) =>
        prev.map((c) => (c.id === id ? res.data.category : c)).sort((a, b) => a.sort_order - b.sort_order)
      );
      setEditingId(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update category');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteArticleCategory(deleteTarget.id);
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to delete category');
    }
  };

  const totalPages = Math.ceil(categories.length / PAGE_SIZE);
  const paginated = categories.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showPagination = totalPages > 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Categories</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Create new category */}
      <Card sx={{ mb: 3, p: 2 }}>
        <form onSubmit={handleCreate}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="New category name (e.g. 'Irrigation')"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <TextField
              size="small"
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              sx={{ width: 60 }}
              title="Category badge color"
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<Add />}
              disabled={submitting || !newName.trim()}
            >
              {submitting ? 'Adding…' : 'Add Category'}
            </Button>
          </Box>
        </form>
      </Card>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search categories…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 360 }}
        InputProps={{
          startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      {/* Categories table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No categories found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {editingId === c.id ? (
                        <TextField size="small" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                      ) : (
                        c.name
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={c.slug} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {editingId === c.id ? (
                        <TextField
                          size="small"
                          type="color"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          sx={{ width: 60 }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: c.color, border: '1px solid #ddd' }} />
                          <Typography variant="caption">{c.color}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === c.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editSort}
                          onChange={(e) => setEditSort(Number(e.target.value))}
                          sx={{ width: 80 }}
                        />
                      ) : (
                        c.sort_order
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editingId === c.id ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => handleSaveEdit(c.id)} disabled={savingEdit}>
                            <Save fontSize="small" color="primary" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => startEdit(c)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {showPagination && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Delete category "{deleteTarget?.name}"?
            <br />
            Articles using this category will keep the old slug in their data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
